import { useCallback, useEffect, useMemo, useState } from 'react';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import questionsAndAnswers, { DeleteRotatingQuestionProps } from '@/services/questionsAndAnswers';
import { FetchApiIds } from '@/types';
import { Question } from '@/types/api';
import RotatingQuestionsListPage from './RotatingQuestionsList.component';

const RotatingQuestionsListContainer = function (): JSX.Element {
  const deleteRotatingQuestionMutation = useApiSubmit<Question, DeleteRotatingQuestionProps>(
    questionsAndAnswers.deleteRotatingQuestion
  );

  const rotatingQuestionsResponse = useApiFetch<Question[]>(
    FetchApiIds.ROTATING_QUESTIONS,
    questionsAndAnswers.getRotatingQuestions
  );
  const [activeQuestions, setActiveQuestions] = useState<Question[]>([]);
  const [inactiveQuestions, setInactiveQuestions] = useState<Question[]>([]);

  const handleDeleteQuestion = useCallback(
    async (questionId: string | number): Promise<void> => {
      try {
        await deleteRotatingQuestionMutation.mutateAsync({ questionId });
        rotatingQuestionsResponse.refetch();
      } catch (error) {
        console.log('Error on deleting a rotating question: ', error);
      }
    },
    [deleteRotatingQuestionMutation, rotatingQuestionsResponse]
  );

  useEffect(() => {
    const questions = rotatingQuestionsResponse.data ?? [];
    const filteredActiveQuestions = questions.filter((question) => question.isActive);
    const filteredInactiveQuestions = questions.filter((question) => !question.isActive);

    setActiveQuestions(filteredActiveQuestions);
    setInactiveQuestions(filteredInactiveQuestions);
  }, [rotatingQuestionsResponse.data]);

  const isLoading = useMemo(
    () => rotatingQuestionsResponse.isLoading || deleteRotatingQuestionMutation.isLoading,
    [deleteRotatingQuestionMutation.isLoading, rotatingQuestionsResponse.isLoading]
  );

  return (
    <RotatingQuestionsListPage
      isLoading={isLoading}
      activeQuestions={activeQuestions}
      inactiveQuestions={inactiveQuestions}
      handleDeleteQuestion={handleDeleteQuestion}
    />
  );
};

export default RotatingQuestionsListContainer;
